import {
  call, put, select, takeLatest,
} from 'redux-saga/effects';
import getProp from 'lodash/get';
import {
  PERFORM_FEATURES_ACTION,
  PERFORM_FEATURES_ACTION_SUCCESS,
  FETCH_FEATURES_SETTINGS,
  FETCH_SPIN_TO_WIN_SETTINGS,
  FETCH_FEATURES_AUDIT_TRAIL,
  SAVE_FEATURE_AUDIT_TRAIL_COMMENT,
} from '../constants';
import {
  LOCK_TICKET_SCAN,
  UNLOCK_TICKET_SCAN,
  SET_TICKET_SCAN_LIMIT,
  SET_SPIN_TO_WIN_LIMIT,
} from '../constants/features_actions';
import {
  performFeaturesActionSuccess,
  performFeaturesActionFailure,
  fetchFeaturesSettingsSuccess,
  fetchFeaturesSettingsFailure,
  fetchSpinToWinSettingsSuccess,
  fetchSpinToWinSettingsFailure,
  fetchFeaturesAuditTrail,
  fetchFeaturesAuditTrailSuccess,
  fetchFeaturesAuditTrailFailure,
  saveFeatureAuditTrailCommentFailure,
} from '../actions';
import {
  featuresActionTypeSelector,
  featuresCommentSelector,
  newTicketscanSelector,
  newSpinToWinSelector,
} from '../selectors/featureActionsSelector';

const createFeaturesSagas = (api) => {
  const {
    fetchFeaturesSettings,
    putTicketscanSettings,
    getDailyLimit,
    setDailyLimit,
  } = api;

  const ACTION_TO_API_METHOD = {
    [LOCK_TICKET_SCAN]: putTicketscanSettings,
    [UNLOCK_TICKET_SCAN]: putTicketscanSettings,
    [SET_TICKET_SCAN_LIMIT]: putTicketscanSettings,
    [SET_SPIN_TO_WIN_LIMIT]: setDailyLimit,
  };

  function* fetchFeaturesSettingsSaga() {
    let response;
    try {
      response = yield call(fetchFeaturesSettings, {});
      const settings = getProp(response.data.data.features, 'ticketscan', {});
      yield put(fetchFeaturesSettingsSuccess({ ticketscanOptions: settings }));
    } catch (error) {
      const statusCode = getProp(error, 'response.status', -1);
      const displayError = getProp(
        error,
        'response.data.errors.0.detail',
        'Data not found',
      );
      yield put(fetchFeaturesSettingsFailure({ errorDetail: displayError, statusCode }));
    }
  }

  function* watchFetchFeaturesSettings() {
    yield takeLatest(FETCH_FEATURES_SETTINGS, fetchFeaturesSettingsSaga);
  }

  function* fetchSpinToWinSettingsSaga() {
    let response;
    try {
      response = yield call(getDailyLimit, {});

      const dailyLimit = getProp(response, 'data.data.dailyLimit', 10);
      yield put(fetchSpinToWinSettingsSuccess({ spinToWinOptions: { dailyLimit } }));
    } catch (error) {
      console.error('Error fetching spin to win settings:', error);
      const statusCode = getProp(error, 'response.status', -1);
      const displayError = getProp(
        error,
        'response.data.errors.0.detail',
        'Data not found',
      );
      yield put(fetchSpinToWinSettingsFailure({ errorDetail: displayError, statusCode }));
    }
  }

  function* watchFetchSpinToWinSettings() {
    yield takeLatest(FETCH_SPIN_TO_WIN_SETTINGS, fetchSpinToWinSettingsSaga);
  }

  function* fetchFeaturesAuditTrailSaga(action) {
    const { auditTrailLimit, auditTrailOffset } = action.payload;

    let response;
    try {
      response = yield call(api.fetchFeaturesAuditTrail, {
        limit: auditTrailLimit,
        offset: auditTrailOffset,
      });
      const { auditLog: auditTrail, totalCount } = getProp(
        response,
        'data.data',
        {},
      );
      yield put(fetchFeaturesAuditTrailSuccess({
        auditTrail,
        auditTrailTotalCount: totalCount,
      }));
    } catch (error) {
      console.log('Fetch audit trail error', error);
      const statusCode = getProp(error, 'response.status', -1);
      yield put(fetchFeaturesAuditTrailFailure({ errorDetail: error, statusCode }));
    }
  }

  function* watchFetchFeaturesAuditTrail() {
    yield takeLatest(FETCH_FEATURES_AUDIT_TRAIL, fetchFeaturesAuditTrailSaga);
  }

  function* saveFeaturesAuditTrailCommentSaga(action) {
    const { comment, auditTrailLimit, auditTrailOffset } = action.payload;
    try {
      const auditTrailEntry = {
        comment,
      };

      yield call(api.postFeaturesAuditTrailComment, {
        body: auditTrailEntry,
      });
      yield put(fetchFeaturesAuditTrail({ auditTrailLimit, auditTrailOffset }));
    } catch (error) {
      console.log('Save audit trail comment error', error);
      const statusCode = getProp(error, 'response.status', -1);
      yield put(saveFeatureAuditTrailCommentFailure({ errorDetail: error, statusCode }));
    }
  }

  function* watchFeaturesSaveAuditTrailComment() {
    yield takeLatest(
      SAVE_FEATURE_AUDIT_TRAIL_COMMENT,
      saveFeaturesAuditTrailCommentSaga,
    );
  }

  function* performFeaturesAction() {
    const featureAction = yield select(featuresActionTypeSelector);
    const comment = yield select(featuresCommentSelector);

    let reqBody;
    let responseData;

    if (featureAction === SET_SPIN_TO_WIN_LIMIT) {
      const newSpinToWinOptions = yield select(newSpinToWinSelector);
      reqBody = {
        dailyLimit: newSpinToWinOptions.dailyLimit,
        comment,
      };
      responseData = 'spinToWinOptions';
    } else {
      const {
        // don't question it
        isUnlimitedScan: isUnlimited,
        ...newTicketscanOptions
      } = yield select(newTicketscanSelector);
      reqBody = {
        ...newTicketscanOptions,
        comment,
        isUnlimited,
      };
      responseData = 'ticketscanOptions';
    }

    const apiMethod = ACTION_TO_API_METHOD[featureAction];
    try {
      const response = yield call(apiMethod, reqBody);
      let options;
      if (featureAction === SET_SPIN_TO_WIN_LIMIT) {
        const dailyLimit = getProp(response, 'data.data.dailyLimit', reqBody.dailyLimit);
        options = { dailyLimit };
      } else {
        options = getProp(response, 'data.data.ticketscan', {});
      }
      yield put(performFeaturesActionSuccess({ [responseData]: options }));
    } catch (error) {
      console.log('Set features options error', error);
      const statusCode = getProp(error, 'response.status', -1);
      yield put(performFeaturesActionFailure({ errorDetail: error, statusCode }));
    }
  }

  function* watchFeaturesSubmissions() {
    yield takeLatest(PERFORM_FEATURES_ACTION, performFeaturesAction);
  }

  function* putRefreshFeatureAudits() {
    yield put(fetchFeaturesAuditTrail({
      auditTrailLimit: 10,
      auditTrailOffset: 0,
    }));
  }

  function* watchPerformFeaturesActionSucccess() {
    yield takeLatest(PERFORM_FEATURES_ACTION_SUCCESS, putRefreshFeatureAudits);
  }

  return {
    watchFeaturesSubmissions,
    watchFetchFeaturesSettings,
    watchPerformFeaturesActionSucccess,
    watchFetchFeaturesAuditTrail,
    watchFeaturesSaveAuditTrailComment,
    watchFetchSpinToWinSettings,
  };
};

export default createFeaturesSagas;
